/**
 * @module project/components/entry_details
 */
import React, { PropTypes } from 'react';
import moment from 'moment';
import SingleEntry from 'common/components/modal/single_entry';
import EntryDetailsComponent from 'project/components/entry_details/entry_details_component';
import EntryDetailsHeader from 'project/components/entry_details/entry_details_header';
import { MOBILE_BREAKPOINT_PX } from 'common/variables';
import screenfull from 'common/components/screenfull';

/**
 * Component for the project single entry extending the base class.
 * @author Kuba Siemiątkowski <kuba@crowdspring.com>
 */
export default class EntryDetails extends SingleEntry {
  constructor(props) {
    super(props);

    this.rateClick = this.rateClick.bind(this);
    this.nextPage = this.nextPage.bind(this);
    this.prevPage = this.prevPage.bind(this);
    this.toggleFullscreen = this.toggleFullscreen.bind(this);
    this.toggleLeftDrawer = this.toggleLeftDrawer.bind(this);
    this.toggleEntryDetails = this.toggleEntryDetails.bind(this);
    this.handleRevisionChange = this.handleRevisionChange.bind(this);
  }

  componentWillReceiveProps(nextProps) {
    if (!nextProps.entry.revisions || !nextProps.entry.revisions.length) {
      const url = `/${nextProps.params.subcategory}/${nextProps.params.pageSlug}/`;
      nextProps.router.push(url);
    }
  }

  componentDidMount() {
    const { fetchComments, entry, currentRevision, drawerState, permissions,
      location } = this.props;
    fetchComments(entry.entry_type, currentRevision);

    /* toggle comments by default if appropriate */
    const shouldToggleComments = (
      !drawerState.leftDrawerVisible &&
      location.query.close_comments !== 'true' &&
      permissions.canComment &&
      window.innerWidth > MOBILE_BREAKPOINT_PX
    );
    if (shouldToggleComments) {
      this.toggleLeftDrawer('comments');
    }
  }

  toggleFullscreen() {
    screenfull.toggle();
  }

  toggleLeftDrawer(type) {
    const { location, router, params, dismissFeedback, baseUrl } = this.props;
    const entryUrl = (
      (baseUrl || `/${params.subcategory}/${params.pageSlug}/entry/`) + `${params.entryId}/`
    );
    const url = location.pathname.indexOf(type) === -1
      ? `${entryUrl}${type}/` : `${entryUrl}?close_comments=true`;

    dismissFeedback();

    return router.push(url);
  }

  rateClick(revId, score) {
    const { scoreRevision, project } = this.props;
    scoreRevision(project.entry_type, revId, score);
  }

  setPage(pageNum) {
    const { dismissFeedback, setPage } = this.props;
    dismissFeedback();
    return setPage(pageNum);
  }

  onNextPage(pageNum) {
    return this.setPage(pageNum);
  }

  onPrevPage(pageNum) {
    return this.setPage(pageNum);
  }

  toggleEntryDetails() {
    const { dismissFeedback, toggleEntryDetails } = this.props;
    dismissFeedback();
    toggleEntryDetails();
  }

  handleRevisionChange(revisionId) {
    const { onChangeRevision, fetchComments, entry } = this.props;
    onChangeRevision(revisionId);
    fetchComments(entry.entry_type, revisionId);
  }

  render() {
    const {
      user, project, entry, revisions, comments, itemsCount, entryState,
      currentPage, params, permissions, currentRevision, scoredRevision, drawerState,
    } = this.props;
    const { leftDrawerVisible, entryCommentsVisible, entryRevisionsVisible } = drawerState;

    const revision = revisions[currentRevision];
    const revisionComments = comments[currentRevision]
      ? Object.values(comments[currentRevision])
      : [];
    let displayedVersion = null;

    if (currentRevision === entry.revisions[0].id) {
      displayedVersion = 'Most Recent';
    } else {
      displayedVersion = moment(revision.created_at).fromNow();
    }

    const detailsState = {
      leftDrawerVisible: leftDrawerVisible || !!scoredRevision,
      entryCommentsVisible,
      entryRevisionsVisible,
      currentRevision,
      displayedVersion,
      zoom: entryState.zoom,
      maxScale: entryState.maxScale,
    };

    return super.render([
      <EntryDetailsHeader
        permissions={permissions}
        detailsState={detailsState}
        project={project}
        user={user.profile_data}
        entry={entry}
        revision={revision}
        comments={revisionComments}
        pageNumber={currentPage}
        itemsCount={itemsCount}
        displayedVersion={displayedVersion}
        onPrevPage={this.prevPage}
        onNextPage={this.nextPage}
        onToggleEntryDetails={this.toggleEntryDetails}
        onToggleLeftDrawer={this.toggleLeftDrawer}
        onDeleteEntry={this.props.deleteEntry}
        onRateClick={this.rateClick}
        toggleRemoveCreativeModal={this.props.toggleRemoveCreativeModal}
        toggleEntryDelete={this.props.toggleEntryDelete}
        toggleEntryReinstate={this.props.toggleEntryReinstate}
        toggleViolationModal={this.props.toggleViolationModal}
        onChangeRevision={this.handleRevisionChange}
        params={params}
        key="header"
      />,
      <EntryDetailsComponent
        project={project}
        user={user.profile_data}
        permissions={permissions}
        detailsState={detailsState}
        entry={entry}
        revision={revision}
        comments={revisionComments}
        onZoomIn={this.props.zoomInEntry}
        onZoomOut={this.props.zoomOutEntry}
        onChangeRevision={this.handleRevisionChange}
        onToggleFullscreen={this.toggleFullscreen}
        onToggleLeftDrawer={this.toggleLeftDrawer}
        scoredRevision={this.props.scoredRevision}
        key="component"
      />
    ]);
  }
}

/**
 * PropTypes.
 *
 * @property {object} project - project data object
 * @property {object} revisions - object with all revisions
 * @property {array} comments - all entry comments
 * @property {object} entryState - object with single entry state
 * @property {object} user - user data object
 * @property {object} permissions - object with user access rights
 * @property {array} visibleEntries - list of all visible entries for current gallery page
 * @property {object} entry - entry data object
 * @property {object} revision - revision id
 * @property {object} allEntries - object with all entries in the state by id
 * @property {number} itemsCount - total number of elements
 * @property {number} pageSize - number of elements per page
 * @property {number} currentGalleryPage - current gallery page number
 * @property {number} currentPage - single entry page number
 * @property {func} fetchComments - action creator for getting entry comments
 * @property {func} scoreRevision - action creator for scoring a revision
 * @property {func} zoomInEntry - action creator to zoom-in the image
 * @property {func} zoomOutEntry - action creator to zoom-out the image
 * @property {func} resetZoom - action creator to reset zoom to 1
 * @property {func} deleteEntry - action creator to remove entry (mark it accordingly)
 * @property {func} toggleEntryDetails - show/hide single entry modal
 * @property {func} dismissFeedback - dismiss revision feedback form
 */
EntryDetails.propTypes = {
  project: PropTypes.object,
  revisions: PropTypes.object,
  comments: PropTypes.object,
  entryState: PropTypes.object,
  user: PropTypes.object,
  permissions: PropTypes.object,
  visibleEntries: PropTypes.array,
  entry: PropTypes.object,
  revision: PropTypes.number,
  allEntries: PropTypes.object,
  itemsCount: PropTypes.number,
  pageSize: PropTypes.number,
  currentGalleryPage: PropTypes.number,
  scoredRevision: PropTypes.number,
  fetchComments: PropTypes.func,
  scoreRevision: PropTypes.func,
  zoomInEntry: PropTypes.func,
  zoomOutEntry: PropTypes.func,
  resetZoom: PropTypes.func,
  deleteEntry: PropTypes.func,
  toggleEntryDetails: PropTypes.func,
  toggleRemoveCreativeModal: PropTypes.func,
  toggleEntryDelete: PropTypes.func,
  toggleEntryReinstate: PropTypes.func,
  dismissFeedback: PropTypes.func,
};



// WEBPACK FOOTER //
// ./project/components/entry_details.jsx